import React from 'react';
import { CheckCircle, AlertTriangle, Briefcase, FileText } from 'lucide-react';

interface DebtorQuickStatsProps {
  debtor: any;
  completeness: {
    complete: number;
    total: number;
    percentage: number;
  };
  documentsCount: number;
}

export default function DebtorQuickStats({
  debtor,
  completeness,
  documentsCount,
}: DebtorQuickStatsProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const getSlikStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      clean: 'Bersih',
      watchlist: 'Waspada',
      blacklist: 'Blacklist',
    };
    return labels[status] || '-';
  };

  const getSlikBadgeStyle = (status: string) => {
    const styles: Record<string, string> = {
      clean: 'bg-green-100 text-green-700',
      watchlist: 'bg-amber-100 text-amber-700',
      blacklist: 'bg-red-100 text-red-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="grid md:grid-cols-4 gap-4">
      {/* Kelengkapan Dokumen */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Kelengkapan Dokumen</p>
            <p className="text-xl font-bold">{completeness.percentage}%</p>
          </div>
          {completeness.percentage === 100 ? (
            <CheckCircle className="w-8 h-8 text-green-500" />
          ) : (
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
          <div
            className="bg-amber-500 h-2 rounded-full transition-all"
            style={{ width: `${completeness.percentage}%` }}
          />
        </div>
      </div>

      {/* Status SLIK */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Status SLIK</p>
            <p className="text-xl font-bold">{getSlikStatusLabel(debtor.slik_status)}</p>
          </div>
          <span
            className={`px-2 py-1 rounded text-sm font-medium ${getSlikBadgeStyle(debtor.slik_status)}`}
          >
            {debtor.slik_score || '-'}
          </span>
        </div>
      </div>

      {/* Penghasilan */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Penghasilan</p>
            <p className="text-lg font-bold">{formatCurrency(debtor.monthly_income)}</p>
          </div>
          <Briefcase className="w-8 h-8 text-blue-500" />
        </div>
      </div>

      {/* Total Dokumen */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Dokumen</p>
            <p className="text-xl font-bold">{documentsCount}</p>
          </div>
          <FileText className="w-8 h-8 text-amber-500" />
        </div>
      </div>
    </div>
  );
}
